import React, { useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight, Eye, EyeOff, Home, LayoutGrid } from 'lucide-react';
import { Button } from '@/components/ui/button';
import CanvasPlayerStage from '../canvas/CanvasPlayerStage';
import { resolveIsOverviewAndTarget } from '../canvas/canvasSelectors';
import type { CanvasBoardDetail } from '../types';

interface Props {
  board: CanvasBoardDetail;
}

/**
 * "Presentatie testen" binnen de editor -- hergebruikt Stap 1's
 * CanvasPlayerStage/CanvasStage ONGEWIJZIGD, maar met LOKALE React-state
 * i.p.v. een live PresentationSession: geen backend-sessie nodig om even te
 * proberen hoe het pad aanvoelt tijdens het bouwen.
 */
export default function CanvasEditorPreview({ board }: Props) {
  const [pathIndex, setPathIndex] = useState(0);
  const [manualOverview, setManualOverview] = useState(false);
  const [blackout, setBlackout] = useState(false);

  const { isOverview, targetId } = useMemo(
    () => resolveIsOverviewAndTarget(board.path, pathIndex, manualOverview),
    [board.path, pathIndex, manualOverview],
  );

  const canGoPrevious = pathIndex > 0;
  const canGoNext = pathIndex < board.path.length - 1;

  const goNext = () => { setManualOverview(false); setPathIndex((i) => Math.min(board.path.length - 1, i + 1)); };
  const goPrevious = () => { setManualOverview(false); setPathIndex((i) => Math.max(0, i - 1)); };
  const goTheme = () => {
    if (!board.theme_frame_id) return;
    const index = board.path.findIndex((step) => step.type === 'focus' && step.target_id === board.theme_frame_id);
    setManualOverview(false);
    setPathIndex(index >= 0 ? index : 0);
  };
  const toggleOverview = () => setManualOverview((prev) => !prev);
  const toggleBlackout = () => setBlackout((prev) => !prev);

  return (
    <div className="flex h-full flex-col gap-3">
      <div className="relative flex-1 overflow-hidden rounded-lg bg-black">
        <CanvasPlayerStage board={board} currentItemId={targetId} isOverview={isOverview} blackout={blackout} />
      </div>
      <div className="flex flex-wrap items-center justify-center gap-2">
        <Button variant="secondary" size="sm" disabled={!canGoPrevious} onClick={goPrevious}>
          <ArrowLeft className="mr-1 h-3.5 w-3.5" /> Vorige
        </Button>
        <Button size="sm" disabled={!canGoNext} onClick={goNext}>
          Volgende <ArrowRight className="ml-1 h-3.5 w-3.5" />
        </Button>
        <Button variant={manualOverview ? 'default' : 'outline'} size="sm" onClick={toggleOverview}>
          <LayoutGrid className="mr-1 h-3.5 w-3.5" /> Overzicht
        </Button>
        <Button variant="outline" size="sm" onClick={goTheme} disabled={!board.theme_frame_id}>
          <Home className="mr-1 h-3.5 w-3.5" /> Thema
        </Button>
        <Button variant={blackout ? 'default' : 'outline'} size="sm" onClick={toggleBlackout}>
          {blackout ? <Eye className="mr-1 h-3.5 w-3.5" /> : <EyeOff className="mr-1 h-3.5 w-3.5" />}
          Blackout
        </Button>
      </div>
    </div>
  );
}
